/**
 * Audit Log Service
 * 
 * Service สำหรับดึงและสร้าง Audit Log
 */

import { getCurrentParseConfig } from '@/services/adapters/parse/parse-client'
import {
  AuditLog,
  AuditLogFilters,
  AuditLogParams,
  AuditLogStatistics,
  CreateAuditLogInput,
} from './audit-log.types'

// ============================================
// Interface
// ============================================

export interface IAuditLogService {
  findAuditLogs(
    sessionToken: string,
    params: AuditLogParams,
    filters?: AuditLogFilters
  ): Promise<{ results: AuditLog[]; count: number }>
  
  getAuditLog(sessionToken: string, id: string): Promise<AuditLog | null>
  
  getStatistics(
    sessionToken: string,
    filters?: AuditLogFilters
  ): Promise<AuditLogStatistics>
  
  createAuditLog(
    sessionToken: string,
    input: CreateAuditLogInput
  ): Promise<AuditLog>
}

// ============================================
// Parse V1 Implementation
// ============================================

class ParseV1AuditLogService implements IAuditLogService {
  
  private async getHeaders(sessionToken: string): Promise<HeadersInit> {
    const config = getCurrentParseConfig();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Parse-Application-Id': config.appId,
      'X-Parse-Session-Token': sessionToken,
    }
    
    // ✅ Add device headers if available (non-blocking)
    try {
      const { fingerprintService } = await import('@/services/fingerprint.service');
      const deviceHeaders = await fingerprintService.getDeviceHeaders();
      Object.assign(headers, deviceHeaders);
      console.log('📱 [AuditLog] Device headers added:', Object.keys(deviceHeaders));
    } catch (deviceError) {
      // Non-critical - continue without device headers
      console.warn('⚠️ [AuditLog] Could not add device headers:', deviceError);
    }
    
    return headers;
  }
  
  private async callCloudFunction(
    functionName: string,
    params: Record<string, any>,
    sessionToken: string
  ): Promise<any> {
    const config = getCurrentParseConfig();
    const url = `${config.serverURL}/functions/${functionName}`
    
    console.log(`📡 [AuditLog] Calling ${functionName}`)
    
    const headers = await this.getHeaders(sessionToken);
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error(`❌ [AuditLog] ${functionName} failed:`, errorData)
      throw new Error(errorData.error || `Cloud function ${functionName} failed`)
    }
    
    const data = await response.json()
    return data.result
  }
  
  async findAuditLogs(
    sessionToken: string,
    params: AuditLogParams,
    filters?: AuditLogFilters
  ): Promise<{ results: AuditLog[]; count: number }> {
    console.log('📊 [AuditLog] Finding audit logs...', { params, filters })
    
    const result = await this.callCloudFunction(
      'findAuditLogs',
      { params, filters },
      sessionToken
    )
    
    // ⭐ กรอง "unknown" ออก (ปัญหาจาก backend สร้าง duplicate)
    // จะแก้หลังบ้านทีหลัง ตอนนี้ซ่อนใน UI ก่อน
    const filteredResults = (result.results as AuditLog[]).filter(log => {
      // ซ่อน records ที่ actorName เป็น "unknown" หรือ "Unknown"
      if (log.actorName?.toLowerCase() === 'unknown') {
        return false
      }
      return true
    })
    
    // คำนวณ count โดยประมาณ (ลด unknown ออก ~proportional)
    const unknownCount = (result.results as AuditLog[]).length - filteredResults.length
    const estimatedTotalCount = Math.max(0, result.count - unknownCount)
    
    console.log(`✅ [AuditLog] Found ${result.count} logs, showing ${filteredResults.length} (hidden ${unknownCount} unknown)`)
    
    // ✅ Debug: ตรวจสอบ fingerprintId ใน logs
    const logsWithFingerprint = filteredResults.filter(log => log.fingerprintId)
    console.log(`🔍 [AuditLog] Logs with fingerprintId: ${logsWithFingerprint.length}/${filteredResults.length}`)
    if (logsWithFingerprint.length > 0) {
      console.log('📋 [AuditLog] Sample fingerprintId:', logsWithFingerprint[0].fingerprintId)
    }
    
    return {
      results: filteredResults,
      count: estimatedTotalCount,
    }
  }
  
  async getAuditLog(sessionToken: string, id: string): Promise<AuditLog | null> {
    console.log('🔍 [AuditLog] Getting audit log:', id)
    
    const result = await this.callCloudFunction(
      'getAuditLog',
      { id },
      sessionToken
    )
    
    return result as AuditLog
  }
  
  async getStatistics(
    sessionToken: string,
    filters?: AuditLogFilters
  ): Promise<AuditLogStatistics> {
    console.log('📊 [AuditLog] Getting statistics...')
    
    const result = await this.callCloudFunction(
      'getAuditLogStatistics',
      { filters },
      sessionToken
    )
    
    return result as AuditLogStatistics
  }
  
  async createAuditLog(
    sessionToken: string,
    input: CreateAuditLogInput
  ): Promise<AuditLog> {
    console.log('📝 [AuditLog] Creating audit log:', {
      action: input.action,
      entityType: input.entityType,
      entityId: input.entityId,
      description: input.description,
    })
    
    // 📝 เรียก Cloud Function (ต้องสร้างใน backend - ดู PROJECT_KNOWLEDGE.md)
    // Backend จะดึง actor info จาก session อัตโนมัติ
    
    try {
      const params = {
        action: input.action,
        entityType: input.entityType,
        entityId: input.entityId,
        entityName: input.entityName,
        description: input.description,
        changes: input.changes || [],
        metadata: input.metadata || {},
        severity: input.severity || 'info',
        status: input.status || 'success',
        errorMessage: input.errorMessage,
      }
      
      console.log('📤 [AuditLog] Calling createAuditLog with params:', params)
      
      const result = await this.callCloudFunction(
        'createAuditLog',
        params,
        sessionToken
      )
      
      console.log('✅ [AuditLog] Created via Cloud Function:', result)
      
      return {
        id: result.id,
        actorId: result.actor_id,
        actorName: result.actor_name || 'Admin',
        actorUsername: result.actor_username || '',
        actorRole: result.actor_role || '',
        ...input,
        timestamp: result.timestamp || new Date().toISOString(),
        changes: input.changes || [],
        category: input.entityType.toLowerCase(),
        severity: input.severity || 'info',
        status: input.status || 'success',
        createdAt: result.createdAt,
      }
    } catch (error) {
      // ⚠️ Cloud Function ยังไม่มี - log warning แต่ไม่ throw error
      // เมื่อ backend สร้าง function แล้วจะทำงานได้อัตโนมัติ
      console.warn('⚠️ [AuditLog] createAuditLog Cloud Function not available:', error)
      console.warn('⚠️ [AuditLog] Please create this function in backend (see PROJECT_KNOWLEDGE.md)')
      
      // Return mock result (audit log จะไม่ถูกบันทึกจนกว่าจะมี Cloud Function)
      return {
        id: `pending-${Date.now()}`,
        actorId: null,
        actorName: 'Admin',
        actorUsername: '',
        actorRole: '',
        ...input,
        timestamp: new Date().toISOString(),
        changes: input.changes || [],
        category: input.entityType.toLowerCase(),
        severity: input.severity || 'info',
        status: input.status || 'success',
        createdAt: new Date().toISOString(),
      }
    }
  }
}

// ============================================
// Mock Implementation (for development)
// ============================================

class MockAuditLogService implements IAuditLogService {
  private mockLogs: AuditLog[] = [
    {
      id: 'mock-1',
      actorId: 'admin-1',
      actorName: 'Admin Test',
      actorUsername: 'admin',
      actorRole: 'super_admin',
      action: 'login',
      entityType: 'User',
      entityId: 'admin-1',
      entityName: 'Admin Test',
      description: 'เข้าสู่ระบบ Admin Panel',
      timestamp: new Date().toISOString(),
      changes: [],
      category: 'auth',
      severity: 'info',
      status: 'success',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'mock-2',
      actorId: 'admin-1',
      actorName: 'Admin Test',
      actorUsername: 'admin',
      actorRole: 'super_admin',
      action: 'create',
      entityType: 'Member',
      entityId: 'member-123',
      entityName: 'ทดสอบ นามสกุล',
      description: 'สร้างสมาชิกใหม่: ทดสอบ นามสกุล (0812345678)',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      changes: [],
      category: 'member',
      severity: 'info',
      status: 'success',
      createdAt: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: 'mock-3',
      actorId: 'admin-1',
      actorName: 'Admin Test',
      actorUsername: 'admin',
      actorRole: 'super_admin',
      action: 'update',
      entityType: 'Member',
      entityId: 'member-456',
      entityName: 'สมชาย ใจดี',
      description: 'แก้ไขข้อมูลสมาชิก: สมชาย ใจดี',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      changes: [
        { field: 'firstname', fieldLabel: 'ชื่อ', oldValue: 'สมชาย', newValue: 'สมชาติ' },
        { field: 'bank_account', fieldLabel: 'เลขบัญชี', oldValue: '1234567890', newValue: '0987654321' },
      ],
      category: 'member',
      severity: 'info',
      status: 'success',
      createdAt: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: 'mock-4',
      actorId: 'admin-1',
      actorName: 'Admin Test',
      actorUsername: 'admin',
      actorRole: 'super_admin',
      action: 'update_setting',
      entityType: 'AppConfig',
      entityId: 'config-1',
      entityName: 'การตั้งค่าระบบ',
      description: 'อัปเดตการตั้งค่าระบบ (หมวด: การเงิน)',
      timestamp: new Date(Date.now() - 86400000).toISOString(),
      changes: [
        { field: 'deposit_min', fieldLabel: 'ฝากขั้นต่ำ', oldValue: 100, newValue: 50 },
      ],
      category: 'setting',
      severity: 'warning',
      status: 'success',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
    },
  ]
  
  async findAuditLogs(
    _sessionToken: string,
    params: AuditLogParams,
    filters?: AuditLogFilters
  ): Promise<{ results: AuditLog[]; count: number }> {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    let results = [...this.mockLogs]
    
    // Apply filters
    if (filters?.action) {
      results = results.filter(log => log.action === filters.action)
    }
    if (filters?.entityType) {
      results = results.filter(log => log.entityType === filters.entityType)
    }
    if (filters?.status) {
      results = results.filter(log => log.status === filters.status)
    }
    if (filters?.searchText) {
      const search = filters.searchText.toLowerCase()
      results = results.filter(log => 
        log.description.toLowerCase().includes(search) ||
        log.actorName.toLowerCase().includes(search) ||
        log.entityName.toLowerCase().includes(search)
      )
    }
    
    const count = results.length
    const start = (params.page - 1) * params.limit
    results = results.slice(start, start + params.limit)
    
    return { results, count }
  }
  
  async getAuditLog(_sessionToken: string, id: string): Promise<AuditLog | null> {
    await new Promise(resolve => setTimeout(resolve, 300))
    return this.mockLogs.find(log => log.id === id) || null
  }
  
  async getStatistics(
    _sessionToken: string,
    _filters?: AuditLogFilters
  ): Promise<AuditLogStatistics> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    return {
      total: this.mockLogs.length,
      byAction: {
        login: 1,
        create: 1,
        update: 1,
        update_setting: 1,
      },
      byEntity: {
        User: 1,
        Member: 2,
        AppConfig: 1,
      },
      bySeverity: {
        info: 3,
        warning: 1,
      },
      byStatus: {
        success: 4,
        failed: 0,
      },
      topActors: [
        { name: 'Admin Test', count: 4 },
      ],
    }
  }
  
  async createAuditLog(
    _sessionToken: string,
    input: CreateAuditLogInput
  ): Promise<AuditLog> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const newLog: AuditLog = {
      id: `mock-${Date.now()}`,
      actorId: 'admin-1',
      actorName: 'Admin Test',
      actorUsername: 'admin',
      actorRole: 'super_admin',
      ...input,
      timestamp: new Date().toISOString(),
      changes: input.changes || [],
      category: input.entityType.toLowerCase(),
      severity: input.severity || 'info',
      status: input.status || 'success',
      createdAt: new Date().toISOString(),
    }
    
    this.mockLogs.unshift(newLog)
    return newLog
  }
}

// ============================================
// Factory
// ============================================

let service: IAuditLogService | null = null

export function getAuditLogService(): IAuditLogService {
  if (service) return service
  
  const adapter = import.meta.env.VITE_API_ADAPTER || 'parse-v1'
  
  if (adapter === 'parse-v1') {
    service = new ParseV1AuditLogService()
  } else {
    service = new MockAuditLogService()
  }
  
  return service
}

export function resetAuditLogService() {
  service = null
}

